import { Flex, Heading, Link, Text } from "@chakra-ui/layout";
import { Button, Tooltip } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { RiSettings5Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { OrganizationDateView } from "src/components/DataDisplay/OrganizationDateView";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { CustomColumnsTable } from "src/components/Table/CustomColumnsTable";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useOrganization } from "src/hooks/useOrganization";
import { usePaginatedRemoteDataQuery } from "src/hooks/usePaginatedRemoteDataQuery";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as breadcrumb from "src/services/breadcrumb";
import * as format from "src/services/format";
import * as Url from "src/services/url";
import { OrgAdmin } from "src/services/url";
import * as GQL from "src/types/graphql";
import { EmptyMatchConfig } from "./components/EmptyMatchConfig";
import { GET_MATCH_RUNS } from "./graphql/queries";

function createElevatedSchoolAdminFilter(
  applyingSchoolIds: string[],
  attendingSchoolIds: string[]
) {
  return {
    _not: {
      schools: {
        school_id: {
          _nin: [...applyingSchoolIds, ...attendingSchoolIds],
        },
      },
    },
  };
}
export const Index = (): React.ReactElement => {
  const organization = useOrganization();
  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();
  const navigate = useNavigate();
  const flags = useFlags(["lottery-position"]);
  const { orderBy, setOrderBy } = useOrderByParams({
    defaultOrderBy: { sortKey: "created_at", sortType: GQL.order_by.desc },
  });

  const { isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds } =
    useSchoolAdmin();

  const { pagination, setPagination } = usePaginationParams();
  const { usePaginatedQuery } = usePaginatedRemoteDataQuery<
    GQL.GetMatchRuns,
    GQL.GetMatchRunsVariables
  >(GET_MATCH_RUNS, {
    organizationId: organization.toNullable()?.id ?? "",
    enrollmentPeriodId: selectedEnrollmentPeriod?.id ?? "",
    limit: pagination.limit,
    offset: pagination.offset,
    order_by: [orderBy ? { [orderBy.sortKey]: orderBy.sortType } : {}],
    school_filter: isElevatedSchoolAdmin
      ? createElevatedSchoolAdminFilter(applyingSchoolIds, attendingSchoolIds)
      : {},
  });
  const { remoteData } = usePaginatedQuery({
    skip: !selectedEnrollmentPeriod || !organization.hasData(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {({ match_run, match_run_aggregate }) => {
        const columns: ColumnDef<GQL.GetMatchRuns_match_run>[] = [
          {
            id: "name",
            header: "Match name",
            cell: (props) => (
              <Link
                color="primary.500"
                href={organization
                  .map((org) =>
                    Url.OrgAdmin.Match.details(org, props.row.original.id)
                  )
                  .withDefault("#")}
                textDecoration="underline"
              >
                {props.row.original.name ?? "Match"}
              </Link>
            ),
          },
          {
            id: "createdAt",
            header: "Date run",
            cell: (props) => (
              <OrganizationDateView isoDate={props.row.original.created_at} />
            ),
          },
          {
            id: "status",
            header: "Status",
            cell: (props) => (
              <Tooltip label={props.row.original.status_message ?? ""}>
                <Text cursor="default">{props.row.original.status}</Text>
              </Tooltip>
            ),
          },
          {
            id: "owner",
            header: "Run by",
            cell: (props) =>
              format.adminName(
                props.row.original.owner?.people[0],
                // Default to "Avela admin" after discussion with product team
                // See: https://avelaed.slack.com/archives/C064CJH1AJV/p1774622276748729
                "Avela admin"
              ),
          },
          ...((flags["lottery-position"].enabled
            ? [
                {
                  id: "publishedAt",
                  header: "Position last saved",
                  cell: (props) => (
                    <OrganizationDateView
                      isoDate={
                        props.row.original.publish_events[0]?.published_at
                      }
                    />
                  ),
                },
                {
                  id: "publishedBy",
                  header: "Position saved by",
                  cell: (props) => {
                    const publishEvent = props.row.original.publish_events[0];
                    if (!publishEvent) return null;
                    return format.adminName(
                      publishEvent.published_by_person,
                      // Default to "Avela admin" after discussion with product team
                      // See: https://avelaed.slack.com/archives/C064CJH1AJV/p1774622276748729
                      "Avela admin"
                    );
                  },
                },
              ]
            : []) satisfies ColumnDef<GQL.GetMatchRuns_match_run>[]),
        ];

        return (
          <Flex direction="column">
            <Breadcrumb
              items={breadcrumb.match.getBreadcrumbsForList(organization)}
              mb={4}
            />
            <Flex justifyContent="space-between" alignItems="center">
              <Heading as="h1" variant="admin">
                Match
              </Heading>
              <WithUserPermissions
                permissions={[
                  "match.configuration:create",
                  "match.configuration:read",
                ]}
              >
                <Button
                  leftIcon={<RiSettings5Line />}
                  onClick={() =>
                    navigate(
                      organization
                        .map((org) => OrgAdmin.Match.configure(org))
                        .withDefault("#")
                    )
                  }
                >
                  Match rules
                </Button>
              </WithUserPermissions>
            </Flex>

            {match_run.length === 0 ? (
              <EmptyMatchConfig />
            ) : (
              <Flex mt={10}>
                <CustomColumnsTable<GQL.GetMatchRuns_match_run>
                  columns={columns}
                  data={match_run}
                  limit={pagination.limit}
                  offset={pagination.offset}
                  count={match_run_aggregate.aggregate?.count ?? 0}
                  sortableColumnIds={["created_at"]}
                  onFetchMore={setPagination}
                  onChangeSort={setOrderBy}
                />
              </Flex>
            )}
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};
